import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { FlaskConical } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';

interface LabExercise {
  id: string;
  title: string;
  description: string;
  content: string;
  order_number: number;
  is_active: boolean;
}

export default function LabExercisesList() {
  const [exercises, setExercises] = useState<LabExercise[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedExercise, setSelectedExercise] = useState<LabExercise | null>(null);

  useEffect(() => {
    fetchExercises();
  }, []);

  const fetchExercises = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('lab_exercises')
        .select('*')
        .eq('is_active', true)
        .order('order_number');
      
      if (error) throw error;
      
      setExercises(data || []);
      
      // Select the first exercise by default if available
      if (data && data.length > 0 && !selectedExercise) {
        setSelectedExercise(data[0]);
      }
    } catch (err) {
      console.error('Error fetching lab exercises:', err);
      setError('Failed to load lab exercises');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="md" />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  if (exercises.length === 0) {
    return (
      <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
        <p className="text-gray-600 text-center">No lab exercises available.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex flex-col md:flex-row">
        {/* Sidebar with exercise list */}
        <div className="w-full md:w-1/3 border-r border-gray-200">
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center">
              <FlaskConical className="h-5 w-5 text-[#F98B3D] mr-2" />
              <h3 className="font-medium text-gray-900">Lab Exercises</h3>
            </div>
          </div>
          <ul className="divide-y divide-gray-200">
            {exercises.map((exercise) => (
              <li key={exercise.id}>
                <button
                  onClick={() => setSelectedExercise(exercise)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors duration-150 ${
                    selectedExercise?.id === exercise.id ? 'bg-gray-50' : ''
                  }`}
                >
                  <div className="flex items-center">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-3 text-xs font-medium text-gray-800">
                      {exercise.order_number}
                    </span>
                    <span className={`font-medium ${
                      selectedExercise?.id === exercise.id ? 'text-[#F98B3D]' : 'text-gray-900'
                    }`}>
                      {exercise.title}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                    {exercise.description}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Main content area */}
        <div className="w-full md:w-2/3 p-6">
          {selectedExercise ? (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{selectedExercise.title}</h2>
              <p className="text-gray-600 mb-6">{selectedExercise.description}</p>
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedExercise.content }}
              />
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">Select a lab exercise to view its content.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}